import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { lastValueFrom, Subject, takeUntil } from "rxjs";
import { SubidaDocumentoComponent } from "src/app/components/subida-documento/subida-documento";
import { LoteCertificadoComponent } from "src/app/components/lote-certificado/lote-certificado";
import { AsociacionFirmaComponent } from "src/app/components/asociacion-firma-documento/asociacion-firma-documento.component";
import { LoteCertificadoService } from "src/app/core/services/lote-certificado.service";
import { LoteDetalleUrlService } from "src/app/core/services/lote-detalle-url.service";
import { LoteService } from "src/app/core/services/lote.service";
import { FirmaStateService } from "src/app/core/states/firma/firma-state.service";
import { Lote } from "src/app/models/Lote.model";
import { k } from "src/environments/properties";
import { LoteCertificado } from "src/app/models/LoteCertificado.model";
import { LoteDetalleUrl } from "src/app/models/LoteDetalleUrl.model";
import { PlantillasService } from "src/app/core/services/plantilla.service";
import { Plantilla } from "src/app/models/Plantilla.model";
import { LoadingService } from "src/app/core/services/loading.service";
import Swal, { SweetAlertIcon } from "sweetalert2";
import { HelpToursService } from "src/app/core/tours/help-tours.service";

@Component({
  selector: "app-batch-configuration",
  templateUrl: "./batch-configuration.component.html",
  styleUrls: ["./batch-configuration.css"],
})
export class BatchConfigurationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Output() firmaChangeStepper: EventEmitter<any> = new EventEmitter<any>();
  @Output() validateModuleUser: EventEmitter<any> = new EventEmitter<any>();

  batchConfigForm: FormGroup;
  private destroy$ = new Subject<void>(); // Usado para desuscribir los Observables al destruir el componente
  idLote: any = null; // Para almacenar el ID del lote
  public loteCertificados: LoteCertificado[] = [];
  public plantillas: Plantilla[] = [];
  lote: Lote;
  public loteCertificados2: LoteCertificado[] = [];

  @ViewChild(SubidaDocumentoComponent)
  subidaDocumentoComponent!: SubidaDocumentoComponent;
  @ViewChild(LoteCertificadoComponent)
  loteCertificadoComponent!: LoteCertificadoComponent;
  @ViewChild(AsociacionFirmaComponent)
  asociacionFirmaComponent!: AsociacionFirmaComponent;
  // Toast
  toast = Swal;

  constructor(
    private fb: FormBuilder,
    private loteService: LoteService,
    private firmaStateService: FirmaStateService,
    private router: Router,
    private route: ActivatedRoute,
    private loteCertificadoService: LoteCertificadoService,
    private loteDetalleUrlService: LoteDetalleUrlService,
    private plantillaService: PlantillasService,
    public loading: LoadingService,
    private tours: HelpToursService,
  ) {}
  ngOnDestroy(): void {
    this.tours.stop();
  }

  ngOnInit(): void {
    this.toastAlert();

    this.route.paramMap
      .pipe(takeUntil(this.destroy$)) // Desuscribe automáticamente al destruir el componente
      .subscribe(async (params) => {
        // 'id' es el nombre del parámetro definido en tu configuración de rutas
        // Por ejemplo: path: 'firmas/firma/:id'
        const id = params.get("id");

        await this.getTemplates();
        if (id != "firma") {
          this.idLote = id; // El '+' convierte el string a número

          // procedemos a precargar la data de los pasos
          await this.precargarData(this.idLote);
          await this.validateUserModule();
        } else {
          this.idLote = null;
        }
      });

    // Inicializamos el formulario reactivo
    this.batchConfigForm = this.fb.group({
      titulo: ["", [Validators.required, Validators.minLength(3)]], // Título es requerido y debe tener al menos 3 caracteres
      notificacion_firmantes: [true], // Checkbox Notificar firma (valor inicial es false)
      firma_por_plantilla: [false],
      es_grupo: [false],
      renombrar_documentos: [false],
      id_plantilla: null,
      disco: ["local"],
    });

    this.initReactions();
    this.listenEsGrupoChanges();
  }

  ngAfterViewInit(): void {
    this.onHelp(true);
  }

  onHelp(force = false) {
    this.tours.start("step1_create_lote_help", force);
  }

  initReactions() {
    const firmaPorPlantillaCtrl = this.batchConfigForm.get(
      "firma_por_plantilla",
    );
    const esGrupoCtrl = this.batchConfigForm.get("es_grupo");
    const renombrarDocCtrl = this.batchConfigForm.get("renombrar_documentos");

    const idPlantillaCtrl = this.batchConfigForm.get("id_plantilla");

    // ✅ Cuando cambia "Firma por plantilla"
    firmaPorPlantillaCtrl?.valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        // 1) Plantilla requerida
        idPlantillaCtrl?.setValidators([Validators.required]);
        idPlantillaCtrl?.updateValueAndValidity();

        // 2) Apagar "es_grupo"
        esGrupoCtrl?.setValue(false, { emitEvent: false });
        renombrarDocCtrl?.setValue(false, { emitEvent: false });
      } else {
        // Quitar required y limpiar valor
        idPlantillaCtrl?.clearValidators();
        idPlantillaCtrl?.setValue(null, { emitEvent: false });
        idPlantillaCtrl?.updateValueAndValidity();
      }
    });

    // ✅ Cuando cambia "Es grupo"
    esGrupoCtrl?.valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        // Apagar "firma_por_plantilla" y limpiar plantilla
        firmaPorPlantillaCtrl?.setValue(false, { emitEvent: false });

        idPlantillaCtrl?.clearValidators();
        idPlantillaCtrl?.setValue(null, { emitEvent: false });
        idPlantillaCtrl?.updateValueAndValidity();
      }
    });
  }

  private listenEsGrupoChanges(): void {
    this.batchConfigForm
      .get("es_grupo")
      ?.valueChanges.subscribe((esGrupo: boolean) => {
        if (!esGrupo) {
          this.batchConfigForm.get("renombrar_documentos")?.setValue(false, {
            emitEvent: false,
          });
        }
      });
  }

  async precargarData(id: number): Promise<any> {
    this.lote = this.firmaStateService.getCurrentState()?.lote;

    if (!this.lote) {
      // sino hemos precargado anteriormente nuestro estado global procedemos a cargar y redireccionar el stepper

      this.lote = (await this.showLote(id)) as Lote;
      this.loteCertificados2 = (await this.indexLoteCertificado(id))
        .body as LoteCertificado[];
      const loteDetalleUrl = await this.indexLoteDetalleUrl();

      this.firmaStateService.updateLote(this.lote as Lote);
      this.firmaStateService.updateLoteCertificado(this.loteCertificados2);
      let stepper = 1;

      if (loteDetalleUrl.length) stepper = 2;
      if (await this.validateLoteCertificado()) stepper = 3;
      if (
        this.lote.estado.nombre_corto == "SDF" ||
        this.lote.estado.nombre_corto == "EPC"
      )
        stepper = 4;
      this.firmaChangeStepper.emit(stepper);
    }

    this.batchConfigForm.setValue({
      disco: this.lote?.disco,
      titulo: this.lote?.titulo,
      notificacion_firmantes: this.lote.notificacion_firmantes,
      firma_por_plantilla: this.lote?.firma_por_plantilla,
      es_grupo: this.lote?.es_grupo,
      renombrar_documentos: this.lote?.renombrar_documentos,
      id_plantilla: this.lote?.plantilla?.id ? this.lote?.plantilla?.id : "",
    });
  }

  async indexLoteDetalleUrl() {
    const response = await lastValueFrom(
      this.loteDetalleUrlService.indexDetalleUrl(this.idLote),
    );
    return response.body as LoteDetalleUrl[];
  }

  async showLote(id: number) {
    const lote = await lastValueFrom(this.loteService.show(id));
    return lote;
  }

  async indexLoteCertificado(id: number) {
    const response = await lastValueFrom(
      this.loteCertificadoService.indexLoteCertificado(id),
    );
    return response;
  }

  onChangeFirmaPorPlantillaCheck(event: Event) {
    const input = event.target as HTMLInputElement;
  }

  onChangeFirmaEsGrupoCheck(event: Event) {
    const input = event.target as HTMLInputElement;
  }

  public async saveFormData(): Promise<boolean> {
    const formValues = this.batchConfigForm.value;
    let msg = "";
    let lote = null;

    if (this.batchConfigForm.valid) {
      if (this.idLote) {
        // PUT
        lote = (await lastValueFrom(
          this.loteService.updLote(formValues, this.idLote),
        )) as Lote;
        this.firmaStateService.updateLote(lote as Lote);
        msg = "¡Configuración actualizada!";
      } else {
        // POST

        lote = (await lastValueFrom(this.loteService.post(formValues))) as Lote;
        this.firmaStateService.updateLote(lote as Lote);
        this.router.navigate([`firmas/${lote.id}`]); // Ejemplo: de /lotes/new a /lotes/31
        msg = "¡Configuración agregada!";
      }

      if (lote) {
        this.alert("success", msg);
        return true;
      } else {
        return false;
      }
    } else {
      this.batchConfigForm.markAllAsTouched(); // Marcar para mostrar errores al usuario
      return false; // Indica que el guardado falló debido a la validación
    }
  }

  async validateUserModule() {
    const response = this.firmaStateService.getCurrentState().lote_certificado;
    const usuarioCreacion =
      this.firmaStateService.getCurrentState().lote?.usuario; // Usuario que creo el lote

    const lista = Array.isArray(response)
      ? (response as LoteCertificado[])
      : [];
    this.loteCertificados = lista;

    const userId = localStorage.getItem(k.user_id) ?? "";

    let existe = false;

    //Si el usuario de la sesión, es el mismo que creo el lote
    existe = usuarioCreacion.id == userId;

    //Si no es quien creo el lote, se valida que sea uno de los usuarios firmantes
    if (this.loteCertificados.length > 0 && !existe) {
      //Se valida que el usuario existe dentro de los firmantes asociados al lote
      existe = lista.some((crt) => String(crt?.usuario?.id) === String(userId));
    }
    this.validateModuleUser.emit(existe);
  }

  async getTemplates() {
    const response = await lastValueFrom(
      this.plantillaService.getPlantillas(100, 100),
    );

    if (response?.body) {
      this.plantillas = response?.body;
    } else {
      this.plantillas;
    }
  }

  toastAlert() {
    this.toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
    });
  }

  alert(icontext: SweetAlertIcon, title: string, text: string = "") {
    this.toast.fire({
      icon: icontext,
      title: title,
      text: text,
    });
  }

  async validateLoteCertificado() {
    // Validación principal (siempre)
    if (this.loteCertificados2.length === 0) {
      // this.alert('warning', 'Debe agregar certificados antes de continuar.');
      return false;
    }

    // Validación adicional solo para firma por plantilla
    if (this.lote.firma_por_plantilla) {
      const validarGrupo = await this.validar();

      if (!validarGrupo) {
        this.alert(
          "warning",
          "Debe asociar todos los certificados antes de continuar.",
        );
        return false;
      }
    }

    return true;
  }

  async validar() {
    let sinAsignar = this.loteCertificados2.find((x: any) => {
      return (
        x.loteDetalleUrlCoordenada &&
        Array.isArray(x.loteDetalleUrlCoordenada) &&
        x.loteDetalleUrlCoordenada.length == 0
      );
    });

    return sinAsignar ? false : true;
  }
}
